import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  TextInput,
  Group,
  Text,
  Stack,
  Loader,
  Button,
  Card,
  Avatar,
  Grid,
  Pagination,
  Checkbox,
  ActionIcon,
  Menu,
  Badge,
  deepMerge,
} from "@mantine/core";
import {
  IconSearch,
  IconTruck,
  IconPlus,
  IconLicense,
  IconCar,
  IconPalette,
  IconUsersGroup,
  IconId,
  IconPackages,
  IconDots,
  IconCurrencyDollar,
  IconDotsVertical,
} from "@tabler/icons-react";
import VehiclesService from "../services/vehicles.service";
import { VehicleResponse } from "types/trip.types";
import CostsInputModal from "../components/CostsInputModal";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { hasPermission } from "utils/permissions.utils";
import { CostResponse } from "types/responses.type";
import { DEFAULT_VEHICLE_COSTS } from "utils/user.utils";
import CostService from "services/cost.service";

const Vehicles = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [vehicles, setVehicles] = useState<VehicleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filteredVehicles, setFilteredVehicles] = useState<VehicleResponse[]>(
    [],
  );
  const [activePage, setActivePage] = useState(1);
  const [selectedVehicles, setSelectedVehicles] = useState<Set<number>>(
    new Set(),
  );
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [costsModalOpen, setCostsModalOpen] = useState(false);
  const [vehicleForCosts, setVehicleForCosts] =
    useState<VehicleResponse | null>(null);
  const isAdmin = hasPermission(user, ["vehicle.manage"]);
  const [vehicleInitialCosts, setVehicleInitialCosts] =
    useState<CostResponse>();
  const itemsPerPage = 10;

  const paginatedVehicles = filteredVehicles.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage,
  );

  useEffect(() => {
    setLoading(true);
    VehiclesService.getVehicles()
      .then((res) => {
        if (res.ok && res.data) {
          setVehicles(res.data);
          setError(null);
        } else {
          setError(res.errorMessage || "Failed to fetch vehicles");
        }
      })
      .catch(() => setError("Failed to fetch vehicles"))
      .finally(() => setLoading(false));
  }, []);

  const toggleSelectVehicle = (vehicleId: number) => {
    setSelectedVehicles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedVehicles.size === filteredVehicles.length) {
      setSelectedVehicles(new Set());
    } else {
      setSelectedVehicles(
        new Set(filteredVehicles.map((v) => v.vehicleId ?? 0)),
      );
    }
  };

  const handleSetCosts = (vehicleId?: number) => {
    let vehicle = null;
    if (vehicleId) {
      vehicle = vehicles.find((c) => c.vehicleId === vehicleId) ?? null;
    }
    setVehicleForCosts(vehicle);
    const costs = deepMerge(DEFAULT_VEHICLE_COSTS, vehicle?.costs ?? {});
    setVehicleInitialCosts({
      costSubject: `VEHICLE:${vehicleId ?? 0}`,
      costs,
    });
    setCostsModalOpen(true);
  };

  const handleSaveCosts = (costsReq: CostResponse) => {
    const costRequests: CostResponse[] = [];
    if (vehicleForCosts) {
      costRequests.push({
        costSubject: `VEHICLE:${vehicleForCosts.vehicleId}`,
        costs: costsReq.costs,
      });
    } else {
      selectedVehicles.forEach((vehicleId) => {
        costRequests.push({
          costSubject: `VEHICLE:${vehicleId}`,
          costs: costsReq.costs,
        });
      });
    }
    setLoading(true);
    CostService.update(costRequests)
      .then((response) => {
        if (response.ok) {
          const updatedVehicles = [...vehicles];
          response.data?.forEach((vehicleCostResponse) => {
            const vehicleId = Number(
              vehicleCostResponse.costSubject?.split(":")[1],
            );
            if (vehicleId) {
              const vehicleIndex = updatedVehicles.findIndex(
                (v) => v.vehicleId === vehicleId,
              );
              if (vehicleIndex >= 0) {
                updatedVehicles[vehicleIndex].costs = vehicleCostResponse.costs;
              }
            }
          });
          setVehicles(updatedVehicles);
        } else {
        }
      })
      .finally(() => {
        setLoading(false);
      });
    setCostsModalOpen(false);
    setVehicleForCosts(null);
  };
  const handleCloseCostsModal = () => {
    setVehicleInitialCosts(undefined);
    setCostsModalOpen(false);
    setVehicleForCosts(null);
  };

  useEffect(() => {
    let filtered = vehicles.filter((vehicle) => {
      const matchesSearch = [
        vehicle.licensePlate,
        vehicle.model,
        vehicle.vehicleType,
        vehicle.color,
        vehicle.externalId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesSelected =
        !showOnlySelected || selectedVehicles.has(vehicle.vehicleId ?? 0);
      return matchesSearch && matchesSelected;
    });
    setFilteredVehicles([...filtered]);
    const totalPage = Math.ceil(filtered.length / itemsPerPage);
    if (activePage > totalPage) {
      console.log({ activePage, totalPage });
      setActivePage(1);
    }
  }, [search, vehicles, showOnlySelected, selectedVehicles]);

  return (
    <Container size="xl" py="xl">
      <Stack>
        <Group justify="space-between" align="center">
          <Title order={2}>Vehicles</Title>
        </Group>
        <Group justify="space-between" align="center">
          {vehicles.length > 0 && (
            <TextInput
              placeholder="Search vehicles..."
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              w={300}
            />
          )}
          <Pagination
            total={Math.ceil(filteredVehicles.length / itemsPerPage)}
            p={activePage}
            onChange={setActivePage}
          />
        </Group>

        {loading && <Loader />}
        {error && <Text c="red">{error}</Text>}
        {!loading && vehicles.length === 0 && (
          <Stack align="center" justify="center" style={{ height: "50vh" }}>
            <IconTruck size={48} color="gray" />
            <Text ta="center" c="dimmed">
              No vehicles found.
            </Text>
          </Stack>
        )}
        {!loading && paginatedVehicles.length > 0 && (
          <>
            {paginatedVehicles.length > 0 && (
              <Group justify="space-between" align="center" mb="md">
                <Group>
                  <Checkbox
                    label={`Select all (${filteredVehicles.length} total)`}
                    checked={selectedVehicles.size === filteredVehicles.length}
                    indeterminate={
                      selectedVehicles.size > 0 &&
                      selectedVehicles.size < filteredVehicles.length
                    }
                    onChange={toggleSelectAll}
                  />
                  {selectedVehicles.size > 0 && (
                    <>
                      <Badge
                        size="lg"
                        variant="light"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowOnlySelected(!showOnlySelected)}
                        title="Click to filter by selected vehicles"
                      >
                        {selectedVehicles.size} selected
                        {showOnlySelected && " (filtered)"}
                      </Badge>
                      <Menu>
                        <Menu.Target>
                          <Button
                            leftSection={<IconDots size={16} />}
                            variant="light"
                            size="sm"
                          >
                            Actions
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {isAdmin && (
                            <Menu.Item
                              leftSection={<IconCurrencyDollar size={14} />}
                              onClick={() => handleSetCosts()}
                            >
                              Set Costs
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          setSelectedVehicles(new Set());
                          setShowOnlySelected(false);
                        }}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </Group>
              </Group>
            )}
            <Grid>
              {paginatedVehicles.map((vehicle) => (
                <Grid.Col
                  key={`vehicle-${vehicle.vehicleId}`}
                  span={{ base: 12, lg: 6 }}
                >
                  <Card
                    shadow="sm"
                    padding="lg"
                    radius="md"
                    withBorder
                    style={{
                      backgroundColor: selectedVehicles.has(
                        vehicle.vehicleId ?? 0,
                      )
                        ? "rgba(59, 130, 246, 0.05)"
                        : undefined,
                      borderColor: selectedVehicles.has(vehicle.vehicleId ?? 0)
                        ? "rgb(59, 130, 246)"
                        : undefined,
                      cursor: "pointer",
                    }}
                  >
                    <Group>
                      {selectedVehicles.size > 0 ? (
                        <Checkbox
                          checked={selectedVehicles.has(vehicle.vehicleId ?? 0)}
                          onChange={() =>
                            toggleSelectVehicle(vehicle.vehicleId ?? 0)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Menu>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              onClick={() =>
                                toggleSelectVehicle(vehicle.vehicleId ?? 0)
                              }
                            >
                              Select
                            </Menu.Item>
                            {isAdmin && (
                              <Menu.Item
                                onClick={() =>
                                  handleSetCosts(vehicle.vehicleId ?? 0)
                                }
                              >
                                Set Cost
                              </Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                      <Avatar color="blue" radius="xl">
                        <IconCar size={24} />
                      </Avatar>
                      <Stack gap={0}>
                        <Text fw={500}>
                          {vehicle.model || "Unknown Model"}
                          {vehicle.licensePlate
                            ? ` (${vehicle.licensePlate})`
                            : ""}
                        </Text>
                        {vehicle.vehicleType && (
                          <Group gap="xs">
                            <IconTruck size={16} color="gray" />
                            <Text>{vehicle.vehicleType}</Text>
                          </Group>
                        )}
                        {vehicle.color && (
                          <Group gap="xs">
                            <IconPalette size={16} color="gray" />
                            <Text size="sm" c="dimmed">
                              {vehicle.color}
                            </Text>
                          </Group>
                        )}
                        {vehicle.capacity !== undefined && (
                          <Group gap="xs">
                            <IconPackages size={16} color="gray" />
                            <Text size="sm" c="dimmed">
                              Capacity: {vehicle.capacity}
                            </Text>
                          </Group>
                        )}
                        {vehicle.externalId && (
                          <Group gap="xs">
                            <IconId size={16} color="gray" />
                            <Text size="sm" c="dimmed">
                              External ID: {vehicle.externalId}
                            </Text>
                          </Group>
                        )}
                      </Stack>
                    </Group>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </>
        )}
      </Stack>

      <CostsInputModal
        initialCosts={vehicleInitialCosts}
        opened={costsModalOpen}
        onClose={handleCloseCostsModal}
        onSave={handleSaveCosts}
        title={
          vehicleForCosts
            ? `Set Costs: ${vehicleForCosts.model || "Unknown Model"} ${
                vehicleForCosts.licensePlate
                  ? ` (${vehicleForCosts.licensePlate})`
                  : ""
              }`
            : "Set Costs for Selected Vehicles"
        }
      />
    </Container>
  );
};

export default Vehicles;
